/**
 * Invitation accept page (W1a-06)
 *
 * Public route — bypasses the auth-required guard in __root.tsx by being
 * registered in `publicRoutes` there. Tokens are submitted via the URL
 * (`/invite/$token`) and exchanged for an active session by calling
 * POST /api/v1/auth/accept-invite.
 *
 * Flow:
 *   1. Mount with `:token` path param.
 *   2. Fetch invitation metadata (`GET /auth/invitation/:token`). On 404
 *      render a friendly "expired or already used" page with a link to
 *      /login.
 *   3. On 200 render the accept form: email (read-only), first/last
 *      name (pre-fill if returned + editable), password + confirm
 *      (with strength meter), submit button.
 *   4. On submit POST to /auth/accept-invite. On 200 stash both tokens
 *      to localStorage (same scheme as /login) and navigate to `/`.
 *   5. On 410 / 422 / 429 render the appropriate error UI.
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye, EyeOff, AlertTriangle, CheckCircle2, Loader2, ShieldCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ParticlesBackground } from '@/components/particles-background';
import { cn } from '@/lib/utils';
import {
  useInvitationMetadata,
  useAcceptInvite,
  type AcceptInviteResponse,
} from '@/lib/use-invitation';

export const Route = createFileRoute('/invite/$token')({
  component: InviteAcceptPage,
});

// ─── Form schema ─────────────────────────────────────────────────────────────

const acceptSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required').max(100),
    lastName: z.string().min(1, 'Last name is required').max(100),
    password: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .max(256, 'Password must be 256 characters or fewer'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type AcceptFormValues = z.infer<typeof acceptSchema>;

// ─── Password strength meter ─────────────────────────────────────────────────
//
// Lightweight client-side heuristic — NOT a security boundary. Server
// enforces the 12-char minimum via Zod. The meter is purely UX:
// encourages a stronger password by surfacing class diversity. Real
// cracking-time estimation would need zxcvbn; deferred to follow-up.

interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: 'Too short' | 'Weak' | 'Fair' | 'Good' | 'Strong';
  color: string;
}

function getPasswordStrength(pwd: string): PasswordStrength {
  if (pwd.length < 12) {
    return { score: 0, label: 'Too short', color: 'bg-destructive' };
  }
  let classes = 0;
  if (/[a-z]/.test(pwd)) classes += 1;
  if (/[A-Z]/.test(pwd)) classes += 1;
  if (/[0-9]/.test(pwd)) classes += 1;
  if (/[^a-zA-Z0-9]/.test(pwd)) classes += 1;
  const longBonus = pwd.length >= 16 ? 1 : 0;
  const total = classes + longBonus;

  if (total <= 1) return { score: 1, label: 'Weak', color: 'bg-destructive' };
  if (total === 2) return { score: 2, label: 'Fair', color: 'bg-amber-500' };
  if (total === 3) return { score: 3, label: 'Good', color: 'bg-green-500' };
  return { score: 4, label: 'Strong', color: 'bg-emerald-500' };
}

// ─── Page component ─────────────────────────────────────────────────────────

function InviteAcceptPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const metadataQuery = useInvitationMetadata(token);
  const acceptMutation = useAcceptInvite();

  const form = useForm<AcceptFormValues>({
    // The pre-existing @hookform/resolvers + zod typings drift on this
    // workspace produces the SAME TS2769 here that routes/login.tsx
    // already triggers — see login.tsx:49 baseline error. Runtime is
    // unaffected. Cast to keep the web baseline at 3 known errors.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(acceptSchema as any) as any,
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    // Re-render the strength meter on every keystroke
    mode: 'onChange',
  });

  const passwordValue = form.watch('password');
  const strength = useMemo(
    () => getPasswordStrength(passwordValue ?? ''),
    [passwordValue]
  );

  const onSubmit = async (values: AcceptFormValues) => {
    setSubmitError(null);
    try {
      const result: AcceptInviteResponse = await acceptMutation.mutateAsync({
        token,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      });

      // Persist tokens — mirror /login flow exactly.
      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);

      navigate({ to: '/' });
    } catch (err) {
      // err is an InvitationApiError. Surface different messages by
      // status — 410 means the invitation is no longer valid; 422 means
      // a body validation issue (unlikely after Zod-side); 429 means
      // rate-limit; everything else is generic.
      const status = (err as { status?: number }).status;
      const message = (err as Error).message;

      if (status === 410) {
        setSubmitError(
          'This invitation is no longer valid. It may have expired, been revoked, or already been used. Contact your admin for a new invitation.'
        );
      } else if (status === 429) {
        setSubmitError(
          'Too many attempts. Please wait a moment and try again.'
        );
      } else if (status === 422) {
        setSubmitError(message || 'Please check your input and try again.');
      } else {
        setSubmitError(
          message || 'Could not accept invitation. Please try again.'
        );
      }
    }
  };

  // ─── Loading state ──────────────────────────────────────────────────────
  if (metadataQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // ─── Error state — invitation invalid / expired / unknown ───────────────
  if (metadataQuery.isError || !metadataQuery.data) {
    const status = metadataQuery.error?.status;

    return (
      <div className="min-h-screen w-full relative overflow-hidden bg-[#0f172a] dark">
        <div className="absolute inset-0 z-0">
          <ParticlesBackground />
        </div>
        <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
          <Card
            // auth-surface (index.css): the one forced-dark palette the sign-in
            // and invitation cards read, regardless of branding theme.
            className="auth-surface w-full max-w-md shadow-2xl bg-[#0f172a]/90 backdrop-blur-sm border-slate-700/50 text-slate-100"
          >
            <CardHeader className="text-center pt-8 pb-4">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                </div>
                <h1 className="text-xl font-bold text-white tracking-tight">
                  Invitation invalid
                </h1>
                <p className="text-sm text-slate-400">
                  {status === 429
                    ? 'Too many invitation lookups from this network. Try again in a minute.'
                    : 'This invitation link is invalid, has expired, or has already been used.'}
                </p>
              </div>
            </CardHeader>
            <CardContent className="pb-8">
              <p className="text-xs text-slate-500 text-center mb-6">
                If you believe this is a mistake, contact your administrator
                to request a new invitation.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800">
                  Go to login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const metadata = metadataQuery.data;
  const isSubmitting = acceptMutation.isPending;

  // ─── Accept form ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen w-full relative overflow-hidden bg-[#0f172a] dark">
      <div className="absolute inset-0 z-0">
        <ParticlesBackground />
      </div>

      <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
        <Card
          // auth-surface (index.css): the one forced-dark palette the sign-in
          // and invitation cards read, regardless of branding theme.
          className="auth-surface w-full max-w-md shadow-2xl bg-[#0f172a]/90 backdrop-blur-sm border-slate-700/50 text-slate-100"
        >
          <CardHeader className="text-center pt-8 pb-4">
            <div className="flex flex-col items-center gap-3">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">
                  You&apos;re invited
                </h1>
                <p className="text-sm text-slate-400 mt-1">
                  <span className="text-slate-200 font-medium">
                    {metadata.invitedByName}
                  </span>{' '}
                  invited you to join
                </p>
                <p className="text-base text-primary font-semibold mt-0.5">
                  {metadata.organizationName}
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                {/* Email — read-only */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300">
                    Email
                  </label>
                  <Input
                    value={metadata.email}
                    readOnly
                    disabled
                    className="bg-slate-800/50 border-slate-700 text-slate-400 cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="First"
                            autoComplete="given-name"
                            disabled={isSubmitting}
                            className={cn(fieldState.error && 'border-destructive')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Last"
                            autoComplete="family-name"
                            disabled={isSubmitting}
                            className={cn(fieldState.error && 'border-destructive')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="At least 12 characters"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            disabled={isSubmitting}
                            className={cn(
                              fieldState.error && 'border-destructive',
                              'pr-10'
                            )}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-200"
                            aria-label={
                              showPassword ? 'Hide password' : 'Show password'
                            }
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>

                      {/* Strength meter */}
                      {passwordValue && passwordValue.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4].map((tick) => (
                              <div
                                key={tick}
                                className={cn(
                                  'h-1 flex-1 rounded-full transition-colors',
                                  tick <= strength.score
                                    ? strength.color
                                    : 'bg-slate-700'
                                )}
                              />
                            ))}
                          </div>
                          <p
                            className={cn(
                              'text-xs',
                              strength.score === 0 && 'text-destructive',
                              strength.score === 1 && 'text-destructive',
                              strength.score === 2 && 'text-amber-400',
                              strength.score >= 3 && 'text-green-400'
                            )}
                          >
                            {strength.label}
                          </p>
                        </div>
                      )}

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Re-enter password"
                            type={showConfirm ? 'text' : 'password'}
                            autoComplete="new-password"
                            disabled={isSubmitting}
                            className={cn(
                              fieldState.error && 'border-destructive',
                              'pr-10'
                            )}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-200"
                            aria-label={
                              showConfirm
                                ? 'Hide confirm password'
                                : 'Show confirm password'
                            }
                          >
                            {showConfirm ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {submitError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/30">
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{submitError}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Accepting…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Accept invitation
                    </>
                  )}
                </Button>
              </form>
            </Form>

            <div className="mt-6 pt-4 border-t border-slate-700/50 text-center text-xs text-slate-500">
              <p>
                By accepting, you confirm you control{' '}
                <span className="text-slate-300">{metadata.email}</span> and
                agree to your organization&apos;s policies.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
